import { IVisibleLegend } from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/type';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import { useIntl, getLocale } from 'umi';

interface ILegendMapItem {
  key: string;
  name: string;
  selected?: boolean;
  dateRangeConfig?: {
    days: number;
    periodType: 'day' | 'week' | 'month';
  };
}

type ILegendMap = Record<string, ILegendMapItem>;

interface IExtraMetric {
  key: string;
  nameEn: string;
  nameCn: string;
  selected?: boolean;
  dateRangeConfig?: {
    days: number;
    periodType: 'day' | 'week' | 'month';
  };
}

export default function useOverviewLocalesIntl(extraMetrics?: IExtraMetric[], options?: {
  visibleLegends?: IVisibleLegend[];
}) {
  const { visibleLegends } = options || {};
  // 日期維度常量
  const DATE_DIMENSION = {
    DAY: 'DAY',
    WEEK: 'WEEK',
    MONTH: 'MONTH',
    PCCONSOLEDAY: 'pcConsoleDay'
  };
  const { DAY, WEEK, MONTH, PCCONSOLEDAY } = DATE_DIMENSION;
  const PAGE_ID = 'overview';
  const PAGE_ID_WEEK = `${PAGE_ID  }_week`;
  const PAGE_ID_MONTH = `${PAGE_ID  }_month`;

  const { formatMessage } = useIntl(); // 中英切换方法
  // Engagement Metrics Trends Day
  const ENGAGEMENT_METRICS_TRENDS = [
    { text: formatMessage({ id: 'overview_daily_DAU' }), value: 'dau' },
    { text: formatMessage({ id: 'overview_New' }), value: 'new' },
    // { text: formatMessage({ id: 'overview_daily_D2/3/7' }), value: 'd237' },
    { text: formatMessage({ id: 'overview_daily_D2' }), value: 'd2' },
    { text: formatMessage({ id: 'overview_daily_D3' }), value: 'd3' },
    { text: formatMessage({ id: 'overview_daily_D7' }), value: 'd7' },
    { text: formatMessage({ id: 'overview_TRU' }), value: 'tru' },
    { text: formatMessage({ id: 'overview_PCU' }), value: 'pcu' },
    { text: formatMessage({ id: 'overview_ACU' }), value: 'acu' },
    { text: formatMessage({ id: 'overview_AOT(min)' }), value: 'aot' },
    // { text: formatMessage({ id: 'overview_daily_weighted_tab' }), value: 'wd237' },
    { text: formatMessage({ id: 'overview_daily_Weighted_D2_short' }), name: formatMessage({ id: 'overview_daily_Weighted_D2_short' }), value: 'weighted_d2', bubble: formatMessage({ id: 'overview_Weighted_D2' }) },
    { text: formatMessage({ id: 'overview_daily_Weighted_D3_short' }), name: formatMessage({ id: 'overview_daily_Weighted_D3_short' }), value: 'weighted_d3', bubble: formatMessage({ id: 'overview_Weighted_D3' }) },
    { text: formatMessage({ id: 'overview_daily_Weighted_D7_short' }), name: formatMessage({ id: 'overview_daily_Weighted_D7_short' }), value: 'weighted_d7', bubble: formatMessage({ id: 'overview_Weighted_D7' }) },
  ];

  // Engagement Metrics Trends Week
  const ENGAGEMENT_METRICS_TRENDS_WEEK = [
    { text: formatMessage({ id: 'overview_weekly_WAU' }), value: 'wau' },
    { text: formatMessage({ id: 'overview_New' }), value: 'new' },
    // { text: formatMessage({ id: 'overview_weekly_W2/3' }), value: 'w23' },
    { text: formatMessage({ id: 'overview_weekly_W2' }), value: 'w2' },
    { text: formatMessage({ id: 'overview_weekly_W3' }), value: 'w3' },
    { text: formatMessage({ id: 'overview_TRU' }), value: 'tru' },
    { text: formatMessage({ id: 'overview_PCU' }), value: 'pcu' },
    { text: formatMessage({ id: 'overview_ACU' }), value: 'acu' },
    { text: formatMessage({ id: 'overview_AOT(min)' }), value: 'aot' },
    // { text: formatMessage({ id: 'overview_weekly_weighted_tab' }), value: 'ww23' },
    { text: formatMessage({ id: 'overview_weekly_Weighted_W2_short' }), name: formatMessage({ id: 'overview_weekly_Weighted_W2_short' }), value: 'weighted_w2', bubble: formatMessage({ id: 'overview_Weighted_W2' }) },
    { text: formatMessage({ id: 'overview_weekly_Weighted_W3_short' }), name: formatMessage({ id: 'overview_weekly_Weighted_W3_short' }), value: 'weighted_w3', bubble: formatMessage({ id: 'overview_Weighted_W3' }) },
    { text: formatMessage({ id: 'overview_weekly_avg_dau' }), value: 'avg.wau' },
  ];

  // Engagement Metrics Trends Month
  const ENGAGEMENT_METRICS_TRENDS_MONTH = [
    { text: formatMessage({ id: 'overview_monthly_MAU' }), value: 'mau' },
    { text: formatMessage({ id: 'overview_New' }), value: 'new' },
    // { text: formatMessage({ id: 'overview_monthly_Month_M2/3' }), value: 'm23' },
    { text: formatMessage({ id: 'overview_monthly_Month_M2' }), value: 'm2' },
    { text: formatMessage({ id: 'overview_monthly_Month_M3' }), value: 'm3' },
    { text: formatMessage({ id: 'overview_TRU' }), value: 'tru' },
    { text: formatMessage({ id: 'overview_PCU' }), value: 'pcu' },
    { text: formatMessage({ id: 'overview_ACU' }), value: 'acu' },
    { text: formatMessage({ id: 'overview_AOT(min)' }), value: 'aot' },
    // { text: formatMessage({ id: 'overview_monthly_weighted_tab' }), value: 'wm23' },
    { text: formatMessage({ id: 'overview_monthly_Weighted_M2_short' }), name: formatMessage({ id: 'overview_monthly_Weighted_M2_short' }), value: 'weighted_m2', bubble: formatMessage({ id: 'overview_Weighted_M2' }) },
    { text: formatMessage({ id: 'overview_monthly_Weighted_M3_short' }), name: formatMessage({ id: 'overview_monthly_Weighted_M3_short' }), value: 'weighted_m3', bubble: formatMessage({ id: 'overview_Weighted_M3' }) },
    { text: formatMessage({ id: 'overview_monthly_avg_dau' }), value: 'avg.mau' },
  ];


  const ENGAGEMENT_METRICS_TRENDS_PC_CONSOLE_DAY = [
    { text: formatMessage({ id: 'overview_daily_DAU' }), value: 'active_users' },
    { text: formatMessage({ id: 'overview_New' }), value: 'new_users' },
    { text: formatMessage({ id: 'pc_overview_daily_D1' }), value: 'd1' },
    { text: formatMessage({ id: 'pc_overview_daily_D2' }), value: 'd2' },
    { text: formatMessage({ id: 'overview_daily_D3' }), value: 'd3' },
    { text: formatMessage({ id: 'overview_daily_D7' }), value: 'd7' },
    { text: formatMessage({ id: 'overview_TRU' }), value: 'total_users' },
    { text: formatMessage({ id: 'overview_PCU' }), value: 'pcu' },
    { text: formatMessage({ id: 'overview_AOT(min)' }), value: 'aot' },
    { text: formatMessage({ id: 'pc_overview_AVG_Login_Counts' }), value: 'alc' },
    // { text: formatMessage({ id: 'pc_overview_Entitlement' }), value: 'all_entitlement' },

  ];

  const REVENUE_METRICS_PC_TRENDS_V2 = [
    { text: formatMessage({ id: 'overview_daily_DAU' }), value: 'active_users' },
    { text: formatMessage({ id: 'overview_New' }), value: 'new_users' },
    { text: formatMessage({ id: 'pc_overview_daily_D1' }), value: 'd1' },
    { text: formatMessage({ id: 'pc_overview_daily_D2' }), value: 'd2' },
    { text: formatMessage({ id: 'overview_daily_D3' }), value: 'd3' },
    { text: formatMessage({ id: 'overview_daily_D7' }), value: 'd7' },
    { text: formatMessage({ id: 'overview_TRU' }), value: 'total_users' },
    { text: formatMessage({ id: 'overview_PCU' }), value: 'pcu' },
    { text: formatMessage({ id: 'overview_ACU' }), value: 'acu' },
  ];

  const ENGAGEMENT_METRICS_TRENDS_V2 = [
    { text: formatMessage({ id: 'overview_daily_v2_playtime' }), value: 'median_online_time' },
    { text: formatMessage({ id: 'overview_daily_v2_avg_playtime' }), value: 'aot_hour' },
    { text: formatMessage({ id: 'overview_daily_v2_session' }), value: 'median_session_time' },
    { text: formatMessage({ id: 'overview_daily_v2_avg_session' }), value: 'avg_session_time' },
  ];

  // 日周月 字母配置
  const METRICS_CONFIG = {
    [DAY]: 'd',
    [WEEK]: 'w',
    [MONTH]: 'm',
    [PCCONSOLEDAY]: 'd',
  };

  // 日周月 字母配置加权
  const WEIGHTED_METRICS_CONFIG = {
    [DAY]: 'wd',
    [WEEK]: 'ww',
    [MONTH]: 'wm',
    [PCCONSOLEDAY]: 'wd',
  };

  // Engagement Metrics Trends Tab List
  const ENGAGEMENT_METRICS_TRENDS_TAB_LIST = {
    [DAY]: ENGAGEMENT_METRICS_TRENDS,
    [WEEK]: ENGAGEMENT_METRICS_TRENDS_WEEK,
    [MONTH]: ENGAGEMENT_METRICS_TRENDS_MONTH,
    [PCCONSOLEDAY]: ENGAGEMENT_METRICS_TRENDS_PC_CONSOLE_DAY
  };

  // Revenue Metrics Trends
  const REVENUE_METRICS_TRENDS = [
    { text: formatMessage({ id: 'overview_Revenue($)' }, { unit: getMonetUnit() }), value: 'revenue' /* unit: '$' */ },
    { text: formatMessage({ id: 'overview_ARPPU($)' }, { unit: getMonetUnit() }), value: 'arppu' },
    { text: formatMessage({ id: 'overview_ARPU($)' }, { unit: getMonetUnit() }), value: 'arpu' },
    { text: formatMessage({ id: 'overview_Payer' }), value: 'payer' },
    { text: formatMessage({ id: 'overview_Payer%' }), value: 'payer_ratio' },
  ];

  // Revenue Metrics Trends
  const REVENUE_METRICS_PC_TRENDS = [
    { text: formatMessage({ id: 'pc_overview_Rev($)' }, { unit: getMonetUnit() }), value: 'net_revenue' /* unit: '$' */ },
    { text: formatMessage({ id: 'pc_overview_MTX_Rev' }, { unit: getMonetUnit() }), value: 'mtx_revenue' },
    { text: formatMessage({ id: 'pc_overview_Units' }), value: 'units_number' },
    { text: formatMessage({ id: 'pc_overview_Units_Rev($)' }, { unit: getMonetUnit() }), value: 'units_revenue' },
    // { text: formatMessage({ id: 'pc_overview_DLC_Rev' }), value: 'dlc_revenue' },
    { text: formatMessage({ id: 'pc_overview_MTX_ARPPU' }), value: 'mtx_arppu' },
    { text: formatMessage({ id: 'pc_overview_MTX_ARPU' }), value: 'mtx_arpu' },
    { text: formatMessage({ id: 'pc_overview_MTX_Payer' }), value: 'mtx_payers' },
    { text: formatMessage({ id: 'pc_overview_MTX_Payer_Rate' }), value: 'mtx_payer_rate' },
    { text: formatMessage({ id: 'pc_overview_MTX' }), value: 'mtx_number' },
  ];

  // Countries / Area Breakdown
  const COUNTRY_AREA_ZONE = [
    { text: formatMessage({ id: 'overview_Country' }), value: 'country' },
    { text: formatMessage({ id: 'overview_Region' }), value: 'region' },
    { text: formatMessage({ id: 'overview_Zone' }), value: 'zone' },
    { text: formatMessage({ id: 'common_Channel' }), value: 'channel' },
  ];

  const COUNTRY_AREA_ZONE_DOWNLOADS = {
    country: {
      DAU: {
        countryName: 'Country',
        countryValueName: 'Average DAU',
        share: 'Share of ALL',
        date: 'DATE',
      },
      Revenue: {
        countryName: 'Country',
        countryValueName: 'Sum Revenue',
        share: 'Share of ALL',
        date: 'DATE',
      },
      NEW: {
        countryName: 'Country',
        countryValueName: 'Sum New',
        share: 'Share of ALL',
        date: 'DATE',
      },
    },
    region: {
      DAU: {
        countryName: 'Region',
        countryValueName: 'Average DAU',
        share: 'Share of ALL',
        date: 'DATE',
      },
      Revenue: {
        countryName: 'Region',
        countryValueName: 'Sum Revenue',
        share: 'Share of ALL',
        date: 'DATE',
      },
      NEW: {
        countryName: 'Region',
        countryValueName: 'Sum New',
        share: 'Share of ALL',
        date: 'DATE',
      },
    },
    zone: {
      DAU: {
        countryName: 'Zone',
        countryValueName: 'Average DAU',
        share: 'Share of ALL',
        date: 'DATE',
      },
      Revenue: {
        countryName: 'Zone',
        countryValueName: 'Sum Revenue',
        share: 'Share of ALL',
        date: 'DATE',
      },
      NEW: {
        countryName: 'Zone',
        countryValueName: 'Sum New',
        share: 'Share of ALL',
        date: 'DATE',
      },
    },
  };


  const CHART_TYPE = {
    COLUMN: 'column',
    LINE: 'line',
    TABLE: 'table',
    VS: 'vs',
    SHOW_TAG: 'showTag',
    CHANGE: 'change'
  };

  const FORM_MAP = {
    RANGE_TIME: 'rangeTime',
    RANGE_TIME_DIFF: 'diff',
    RANGE_TIME_TYPE: 'timeType',
    LANGUAGE: 'language',
    ZONE: 'zone',
    REGION: 'region',
    SYSTEM: 'system',
    CHANNEL: 'channel',
    IS_INIT: 'isInit',
  };

  const RESET = 'reset';
  const CONTROL = 'control';

  const FORM_KEYS = ['rangeTime', 'zone', 'region', 'system', 'channel'];

  // 暂不需要转换中英文
  const SYSTEM_LIST = [
    {
      text: 'Android',
      value: '1',
    },
    {
      text: 'IOS',
      value: '2',
    },
    {
      text: 'Web',
      value: '3',
    },
    {
      text: 'Linux',
      value: '4',
    },
    {
      text: 'Windows',
      value: '5',
    },
    {
      text: 'Nintendo',
      value: '6',
    },
    {
      text: 'Mac',
      value: '7',
    },
    {
      text: 'Playstation',
      value: '8',
    },
    {
      text: 'XBox',
      value: '9',
    },
  ];

  /**
   * 根据 visibleLegends 过滤并重命名图例配置
   * @param map 原始图例配置映射
   * @returns 处理后的图例配置映射
   */
  const processLegendMap = (map: Record<string, unknown>) => {
    if (!visibleLegends || visibleLegends.length === 0) return map;
    const filteredMap = {};
    const isEn = getLocale() === LANGUAGE_EN;

    visibleLegends.forEach((item) => {
      // 在原始配置中查找匹配 key 的项
      const foundEntryKey = Object.keys(map).find((k) => map?.[k]?.key === item.key);
      if (foundEntryKey) {
        const customName = getName(isEn, item.nameEn, item.nameCn);
        filteredMap[foundEntryKey] = {
          ...map[foundEntryKey],
          name: customName ?? '',
        };
      }
    });

    return filteredMap;
  };

  const METRICS_LEGEND_MAP = processLegendMap({
    THIS_DATE: {
      key: 'to_date',
      name: formatMessage({ id: 'overview_daily_Today' }),
    },
    LAST_DATE: {
      key: 'last_week',
      name: formatMessage({ id: 'overview_daily_Last_Week' }),
    },
    LAST_DATE_MONTH: {
      key: 'last_month',
      name: formatMessage({ id: 'overview_daily_Last_Month' }),
      selected: false,
    },
    LAST_DATE_YEAR: {
      key: 'last_year',
      name: formatMessage({ id: 'overview_daily_Last_Year' }),
      selected: false,
    },
  });

  const METRICS_LEGEND_MAP_WEEK = processLegendMap({
    THIS_DATE: {
      key: 'to_week',
      name: formatMessage({ id: 'overview_weekly_This_Week' }),
    },
    LAST_DATE: {
      key: 'last_month',
      name: formatMessage({ id: 'overview_weekly_Last_Month' }),
    },
  });

  const METRICS_LEGEND_MAP_MONTH = processLegendMap({
    THIS_DATE: {
      key: 'this_year',
      name: formatMessage({ id: 'overview_monthly_This_Month' }),
    },
    LAST_DATE: {
      key: 'last_year',
      name: formatMessage({ id: 'overview_monthly_Last_Year' }),
    },
  });

  // 生成额外的指标映射
  const generateExtraMetricsMap = (extraMetrics: any[] = []) => {
    const locale = getLocale();
    const isEn = locale === LANGUAGE_EN;
    const extraMap: any = {};
    
    extraMetrics?.forEach((metric) => {
      const key = metric.key.toUpperCase().replace(/_/g, '_');
      extraMap[key] = {
        key: metric.key,
        name: getName(isEn, metric?.nameEn, metric?.nameCn),
        selected: metric?.selected !== false,
        dateRangeConfig: metric?.dateRangeConfig, // 保留日期区间配置
      };
    });
    
    return extraMap;
  };

  const extraMetricsMap = generateExtraMetricsMap(extraMetrics);

  const PC_CONSOLE_MAP = processLegendMap({
    THIS_DATE: {
      key: 'value',
      name: formatMessage({ id: 'overview_daily_Today' }),
    },
    LAST_DATE: {
      key: 'last_value',
      name: formatMessage({ id: 'overview_daily_Last_Week' }),
    },
    ...extraMetricsMap,
  });

  const PC_CONSOLE_MAP_WEEK = processLegendMap({
    THIS_DATE: {
      key: 'value',
      name: formatMessage({ id: 'overview_weekly_This_Week' }),
    },
    LAST_DATE: {
      key: 'last_value',
      name: formatMessage({ id: 'overview_weekly_Last_Month' }),
    },
    ...extraMetricsMap,
  });

  const PC_CONSOLE_MAP_MONTH = processLegendMap({
    THIS_DATE: {
      key: 'value',
      name: formatMessage({ id: 'overview_monthly_This_Month' }),
    },
    LAST_DATE: {
      key: 'last_value',
      name: formatMessage({ id: 'overview_monthly_Last_Year' }),
    },
    ...extraMetricsMap,
  });

  const PC_CONSOLE_MAP_INCLUDED_LAST_WEEK_MONTH = processLegendMap({
    THIS_DATE: {
      key: 'value',
      name: formatMessage({ id: 'overview_daily_Today' }),
    },
    LAST_DATE: {
      key: 'last_value',
      name: formatMessage({ id: 'overview_daily_Last_Week' }),
    },
    LAST_DATE_MONTH: {
      key: 'last_month_value',
      name: formatMessage({ id: 'overview_daily_Last_Month' }),
      selected: false,
    },
    LAST_DATE_YEAR: {
      key: 'last_year_value',
      name: formatMessage({ id: 'overview_daily_Last_Year' }),
      selected: false,
    },
    ...extraMetricsMap,
  });

  const LINE_METRICS_LEGEND_MAP: any = {
    [DAY]: METRICS_LEGEND_MAP,
    [WEEK]: METRICS_LEGEND_MAP_WEEK,
    [MONTH]: METRICS_LEGEND_MAP_MONTH,
    [PCCONSOLEDAY]: PC_CONSOLE_MAP,
  };

  const PC_LINE_METRICS_LEGEND_MAP: Record<string, ILegendMap> = {
    [DAY]: PC_CONSOLE_MAP,
    [WEEK]: PC_CONSOLE_MAP_WEEK,
    [MONTH]: PC_CONSOLE_MAP_MONTH,
  };

  const PC_LEGEND_LAST_WM = {
    [DAY]: PC_CONSOLE_MAP_INCLUDED_LAST_WEEK_MONTH,
  };

  const METRICS_COLORS = ['#5086F3', '#77E097', '#6A7597', '#F8CC6A', '#A08FF2', '#5EC8F1', '#CF61AC', '#F19E55', '#387F7D', '#F17C86', '#8EB9FF'];


  /**
   * 指标的全称解释与变化幅度统计逻辑
   */
  const INDICATOR_MEANING = ['DAU', 'Revenue', 'New', 'D2(T-1)', 'ARPU', 'Payer%']; // 暂时不需要转换

  /**
   * 根据接口metrics_name枚举
   * 替换title展示
   * 主数据显示单位font(前)、end(后)、type*(浮窗数据是否加% 枚举:int percent)
   */
  const CARD_TYPE = {
    [INDICATOR_MEANING[0]]: {
      title: 'DAU',
      font: '',
      end: '',
      type: 'int',
    },
    [INDICATOR_MEANING[1]]: {
      title: 'Revenue',
      font: '',
      end: '',
      type: 'int',
      //  unit: '$'
    },
    [INDICATOR_MEANING[2]]: {
      title: 'New',
      font: '',
      end: '',
      type: 'int',
    },
    [INDICATOR_MEANING[3]]: {
      title: 'D2(T-1)',
      font: '',
      end: '',
      type: 'percent',
    },
    [INDICATOR_MEANING[4]]: {
      title: 'ARPU',
      font: '',
      end: '',
      type: 'int',
    },
    'PayerRatio%': {
      title: 'Payer%',
      font: '',
      end: '%',
      type: 'int',
    },
    [INDICATOR_MEANING[5]]: {
      title: 'Payer%',
      font: '',
      end: '%',
      type: 'percent',
    },
  };

  /**
   * 需求添加($)的指标
   */
  const DOLLAR_METRICS = ['Revenue', 'ARPU', 'ARPPU', 'new users ARPPU']; // 暂时不需要转换

  // 分解图指标常量
  const BREAKDOWN_METRICS = {
    DAY: {
      AU: formatMessage({ id: 'overview_daily_DAU' }),
      BREAKDOWN_AND_NEWUSERS_TITLE: [formatMessage({ id: 'overview_daily_DAU_Breakdown' }), formatMessage({ id: 'overview_Channels_of_New_Users' })],
      BREAKDOWN_TOTAL: 'dau_breakdown_total',
      BREAKDOWN: 'dau_breakdown',
    },
    WEEK: {
      AU: formatMessage({ id: 'overview_weekly_WAU' }),
      BREAKDOWN_AND_NEWUSERS_TITLE: [formatMessage({ id: 'overview_weekly_WAU_Breakdown' }), formatMessage({ id: 'overview_Channels_of_New_Users' })],
      BREAKDOWN_TOTAL: 'wau_breakdown_total',
      BREAKDOWN: 'wau_breakdown',
    },
    MONTH: {
      AU: formatMessage({ id: 'overview_monthly_MAU' }),
      BREAKDOWN_AND_NEWUSERS_TITLE: [formatMessage({ id: 'overview_monthly_MAU_Breakdown' }), formatMessage({ id: 'overview_Channels_of_New_Users' })],
      BREAKDOWN_TOTAL: 'mau_breakdown_total',
      BREAKDOWN: 'mau_breakdown',
    },
  };
  /**
   * 根据接口metrics_name枚举
   * 替换title展示
   * 主数据显示单位font(前)、end(后)、type*(浮窗数据是否加% 枚举:int percent)
   */
  const METRICS_INFO = {
    Revenue: {
      title: formatMessage({ id: 'overview_Revenue' }),
      avgTitle: formatMessage({ id: 'overview_Revenue' }),
      font: '',
      end: `(${getMonetUnit()})`,
      type: 'int',
      decimal: 2,
    },
    New: {
      title: formatMessage({ id: 'overview_New' }),
      avgTitle: formatMessage({ id: 'overview_New' }),
      font: '',
      end: '',
      type: 'int',
    },
    ARPU: {
      title: formatMessage({ id: 'overview_ARPU' }),
      avgTitle: formatMessage({ id: 'overview_ARPU' }),
      font: '',
      end: `(${getMonetUnit()})`,
      type: 'int',
      decimal: 2,
    },
    'PayerRatio%': {
      title: formatMessage({ id: 'overview_Payer%' }),
      avgTitle: formatMessage({ id: 'overview_Payer%' }),
      font: '',
      end: '%',
      type: 'int',
    },
    'Payer%': {
      title: formatMessage({ id: 'overview_Payer%' }),
      avgTitle: formatMessage({ id: 'overview_Payer%' }),
      font: '',
      end: '',
      type: 'percent',
      decimal: 2,
    },
    DAU: {
      title: formatMessage({ id: 'overview_daily_DAU' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Avg_DAU' }),
      font: 'Avg.',
      end: '',
      type: 'int',
    },
    'D2(T-1)': {
      title: formatMessage({ id: 'overview_daily_D2(T-1)' }),
      avgTitle: formatMessage({ id: 'overview_daily_D2(T-1)' }),
      font: '',
      end: '',
      type: 'percent',
      decimal: 2,
    },
    WAU: {
      title: formatMessage({ id: 'overview_weekly_WAU' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Avg_WAU' }),
      font: 'Avg.',
      end: '',
      type: 'int',
    },
    'W2(T-1)': {
      title: formatMessage({ id: 'overview_weekly_W2(T-1)' }),
      avgTitle: formatMessage({ id: 'overview_weekly_W2(T-1)' }),
      font: '',
      end: '',
      type: 'percent',
      decimal: 2,
    },
    MAU: {
      title: formatMessage({ id: 'overview_monthly_MAU' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Avg_MAU' }),
      font: 'Avg.',
      end: '',
      type: 'int',
    },
    'M2(T-1)': {
      title: formatMessage({ id: 'overview_monthly_Month_M2(T-1)' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Month_M2(T-1)' }),
      font: '',
      end: '',
      type: 'percent',
      decimal: 2,
    },

  };
  const METRICS_NAME_DAY = [
    formatMessage({ id: 'overview_daily_DAU' }),
    formatMessage({ id: 'overview_Revenue' }),
    formatMessage({ id: 'overview_New' }),
    formatMessage({ id: 'overview_daily_D2(T-1)' }),
    formatMessage({ id: 'overview_ARPU' }),
    formatMessage({ id: 'overview_Payer%' }),
  ];
  const METRICS_NAME_WEEK = [
    formatMessage({ id: 'overview_weekly_WAU' }),
    formatMessage({ id: 'overview_Revenue' }),
    formatMessage({ id: 'overview_New' }),
    formatMessage({ id: 'overview_weekly_W2(T-1)' }),
    formatMessage({ id: 'overview_ARPU' }),
    formatMessage({ id: 'overview_Payer%' }),
  ];
  const METRICS_NAME_MONTH = [
    formatMessage({ id: 'overview_monthly_MAU' }),
    formatMessage({ id: 'overview_Revenue' }),
    formatMessage({ id: 'overview_New' }),
    formatMessage({ id: 'overview_monthly_Month_M2(T-1)' }),
    formatMessage({ id: 'overview_ARPU' }),
    formatMessage({ id: 'overview_Payer%' }),
  ];

  const MOMENT_DATE_ID = {
    [DATE_DIMENSION.DAY]: 'days',
    [DATE_DIMENSION.WEEK]: 'weeks',
    [DATE_DIMENSION.MONTH]: 'months',

  };

  const EXPLAIN_MAP = {
    dod: 'Day on Day',
    sdlw: 'Same Day Last Week',
    wow: 'Week on Week',
    swlm: 'Same Week Last Month',
    mom: 'Month on Month',
    smly: 'Same Month Last Year',
  };

  return {
    PAGE_ID,
    PAGE_ID_WEEK,
    EXPLAIN_MAP,
    PAGE_ID_MONTH,
    ENGAGEMENT_METRICS_TRENDS,
    METRICS_CONFIG,
    WEIGHTED_METRICS_CONFIG,
    ENGAGEMENT_METRICS_TRENDS_TAB_LIST,
    COUNTRY_AREA_ZONE_DOWNLOADS,
    REVENUE_METRICS_TRENDS,
    REVENUE_METRICS_PC_TRENDS,
    COUNTRY_AREA_ZONE,
    CHART_TYPE,
    FORM_MAP,
    RESET,
    CONTROL,
    SYSTEM_LIST,
    METRICS_LEGEND_MAP,
    LINE_METRICS_LEGEND_MAP,
    PC_LINE_METRICS_LEGEND_MAP,
    METRICS_COLORS,
    INDICATOR_MEANING,
    METRICS_INFO,
    METRICS_NAME_DAY,
    METRICS_NAME_WEEK,
    METRICS_NAME_MONTH,
    DATE_DIMENSION,
    BREAKDOWN_METRICS,
    MOMENT_DATE_ID,
    ENGAGEMENT_METRICS_TRENDS_V2,
    REVENUE_METRICS_PC_TRENDS_V2,
    PC_LEGEND_LAST_WM
  };


}


